import { createRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Check, Copy, Share2, Store } from "lucide-react";
import { useEffect, useState } from "react";

import { rootRoute } from "./root.tsx";
import { useIsDesktop } from "../lib/viewport.ts";
import { DesktopShop } from "./desktop/shop.tsx";
import { api } from "../api/client.ts";
import { useProducts } from "../api/queries.ts";
import { SubPage } from "../components/layout.tsx";
import { Empty, ErrorBlock, Spinner } from "../components/ui.tsx";
import { ProductGrid, SellerNote } from "../components/product-grid.tsx";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { shareOrigin } from "../lib/site-config.ts";

/**
 * 店铺页。
 *
 * 🔴 **这是这个平台跟普通商城最不一样的一页。**
 *    普通商城里一件货全网一个价；这里同一件货在不同的店里价格不同 ——
 *    因为价格是「商品 × 看它的人」算出来的。
 *
 * 🔴 **价格按谁算，必须写在页面上。**
 *    - 没有归属的人（游客/新客）：按这家店的价看，**从这里下单就归属这家店**
 *    - 已有归属的人：按他自己的链算，页面明说"你的专属服务商是 XX"
 *    不说的话，用户会觉得"这家店怎么跟别家一个价"，或者以为被区别对待了。
 */

interface ShopView {
  id: number;
  name: string;
  type: string;
  levelKey: string;
  displayName: string;
  inviteCode: string;
  orderCount: number;
  since: string;
  isMine: boolean;
  boundTo: { id: number; name: string } | null;
}

const TYPE_LABEL: Record<string, string> = { dealer: "经销商", distributor: "分销商" };

function Shop() {
  const { id } = useParams({ from: "/shop/$id" });
  const shopId = Number(id);
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const {
    data: shop,
    isPending,
    error,
  } = useQuery({
    queryKey: ["shop", shopId],
    queryFn: () => api.get<ShopView>(`/shops/${shopId}`),
    retry: false,
  });

  /**
   * 这家店自己的分类条（「主推」「工装常用」）。
   * ⚠️ 跟顶部的全站品类是两回事 —— 这是**店主自己摆的货架**。
   * ⚠️ 只出有货的分类：建了还没放货的点进去是空的，不如不显示。
   */
  const shopCats = useQuery({
    queryKey: ["shop", shopId, "categories"],
    queryFn: () =>
      api.get<
        { id: number; name: string; productCount: number; platformCategoryId: number | null }[]
      >(`/shops/${shopId}/categories`),
    enabled: Boolean(shop),
    retry: false,
  });
  const [catId, setCatId] = useState<number | null>(null);
  const activeCat = shopCats.data?.find((c) => c.id === catId) ?? null;

  /**
   * ⚠️ 带上 shopId —— 没归属的访问者要按这家店的链算价。
   *
   * 🔴 **两种货架用两种筛法**：
   *    - 绑了平台品类的（自动货架）→ 按 `categoryId` 筛，
   *      这样上游新上的货自己就进来了，不用店主再归一次类。
   *    - 自定义货架 → 按 `shopCategoryId` 走关联表。
   */
  const products = useProducts(
    {
      sort: "sales",
      limit: 30,
      shopId,
      ...(activeCat?.platformCategoryId
        ? { categoryId: activeCat.platformCategoryId }
        : { shopCategoryId: catId ?? undefined }),
    },
    Boolean(shop),
  );

  /**
   * 🔴 **进了别人的店要被送回自己的店，不是甩一个错就完。**
   *    一个用户只能看见自己的所属商家 —— 但他可能是点了别人转发的链接进来的，
   *    对他来说这是一次正常的点击，不该以一个红色报错收场。
   *    服务端在 details 里带回了他自己的店 id，这里直接跳过去。
   */
  const boundShopId = (error as { details?: { boundShopId?: number } } | null)?.details
    ?.boundShopId;
  useEffect(() => {
    if (boundShopId) {
      navigate({ to: "/shop/$id", params: { id: String(boundShopId) }, replace: true });
    }
  }, [boundShopId, navigate]);

  if (isPending || boundShopId) {
    return (
      <SubPage title="店铺">
        <Spinner />
      </SubPage>
    );
  }
  if (error || !shop) {
    return (
      <SubPage title="店铺">
        <ErrorBlock error={error ?? new Error("店铺不存在")} />
      </SubPage>
    );
  }

  const shareUrl = `${shareOrigin()}/shop/${shop.id}`;

  return (
    <SubPage title={shop.displayName}>
      {/* 店招 */}
      <div className="bg-gradient-to-b from-brand-600 to-brand-500 px-4 pt-4 pb-8">
        <div className="flex items-center gap-3">
          <span className="flex size-14 shrink-0 items-center justify-center rounded-xl bg-white/20 backdrop-blur">
            <Store className="size-7 text-white" />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-[18px] font-medium text-white">{shop.displayName}</h1>
            <div className="mt-1 flex items-center gap-1.5">
              <span className="rounded bg-white/20 px-1.5 py-0.5 text-[11px] text-white">
                {TYPE_LABEL[shop.type] ?? shop.type}
              </span>
              {shop.levelKey && (
                <span className="rounded bg-white/20 px-1.5 py-0.5 text-[11px] text-white">
                  {shop.levelKey}
                </span>
              )}
              <span className="text-[11px] text-white/75 tabular-nums">
                成交 {shop.orderCount} 单
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3 px-3 pb-4">
        {/*
          🔴 归属说明。这一块骑在店招上，是这一页最要紧的信息 ——
             它解释了"为什么这里的价是这个价"。
        */}
        <Card className="-mt-5 px-4 py-3">
          <SellerNote
            shopName={shop.displayName}
            boundToName={shop.boundTo?.name}
            isMine={shop.isMine}
          />
          {!shop.boundTo && (
            <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
              专属服务商负责你的售后与咨询。一旦确定就不会被别人改，
              换人需要联系平台客服。
            </p>
          )}
        </Card>

        {/* 分享。⚠️ 这是分销商裂变的主要动作，要放在他自己能一眼看到的位置。 */}
        {shop.isMine && (
          <Card className="flex items-center gap-2 px-4 py-3">
            <Share2 className="size-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate text-[12px] text-muted-foreground">
              {shareUrl}
            </span>
            <button
              className="flex shrink-0 items-center gap-1 rounded-md bg-secondary px-2.5 py-1.5 text-[12px] active:opacity-70"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(shareUrl);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                } catch {
                  /** ⚠️ 非 https 下 clipboard 不可用，静默失败即可 —— 链接就在眼前，他能自己选。 */
                }
              }}
            >
              {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
              {copied ? "已复制" : "复制"}
            </button>
          </Card>
        )}

        <div className="flex items-center justify-between px-1 pt-1">
          <h2 className="text-[15px] font-medium">店内商品</h2>
          {products.data && (
            <Badge variant="secondary">{products.data.items.length} 件</Badge>
          )}
        </div>

        {/*
          🔴 **店主自己摆的货架** —— 只有一个分类时不显示这一条：
             一个"全部 + 主推"的切换毫无信息量，白占一行。
        */}
        {(shopCats.data?.length ?? 0) > 1 && (
          <div className="-mx-3 flex gap-2 overflow-x-auto px-3 pb-1 no-scrollbar">
            {[{ id: 0, name: "全部" }, ...(shopCats.data ?? [])].map((c) => {
              const active = (catId ?? 0) === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setCatId(c.id === 0 ? null : c.id)}
                  className={
                    "shrink-0 rounded-full px-3.5 py-1.5 text-[13px] whitespace-nowrap " +
                    (active ? "bg-brand-600 text-white" : "bg-secondary text-muted-foreground")
                  }
                >
                  {c.name}
                </button>
              );
            })}
          </div>
        )}

        {products.isPending ? (
          <Spinner rows={3} />
        ) : products.error ? (
          <ErrorBlock error={products.error} />
        ) : products.data.items.length === 0 ? (
          /* ⚠️ 筛出来是空的时候别说"还没上架" —— 那是两回事，用户会以为整家店是空的。 */
          catId ? (
            <Empty title="这个分类下还没有商品" hint="换一个分类看看" />
          ) : (
            <Empty title="这家店还没有上架商品" hint="平台正在补货，过两天再来看看" />
          )
        ) : (
          <ProductGrid
            items={products.data.items}
            shop={{ id: shop.id, name: shop.displayName }}
          />
        )}
      </div>
    </SubPage>
  );
}

/** 手机版 / 网页版是两套页面，按视口挑一套（URL 与数据 hooks 两边共用）。 */
function ShopRoute() {
  return useIsDesktop() ? <DesktopShop /> : <Shop />;
}

export const shopRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/shop/$id",
  component: ShopRoute,
});
